/**
 * Settings → Billing & credits: the balance first, packs bought on the page, auto top-up as a rule,
 * the activity ledger and Stripe's receipts. Credits only; the pack cards are the one place a price
 * may appear (decision D1).
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CreditCard, KeyRound, Loader2, X } from 'lucide-react';
import api, { billingAPI, type CreditPacksResponse, type WalletBalance } from '../../services/api';
import { aboutVideos, videosFor } from '../../config/pricing';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CreditPackPicker } from '../billing/CreditPackPicker';
import { CreditActivity, ACTIVITY_ANCHOR, formatCreditAmount } from '../billing/CreditActivity';
import { SettingsPanel } from './SettingsPanel';

const BUY_ANCHOR = 'buy';
const WELCOME_CREDITS_LABEL = 'Welcome credits';

const HOLD_MESSAGE = {
  NEGATIVE_BALANCE: 'On hold after a refunded payment left the balance below zero. Buy credits to continue.',
  DISPUTE: 'On hold while a payment dispute is open. It lifts when the dispute is resolved in your favour, or contact support.',
  ADMIN: 'On hold by 4K Labs support. Contact support to lift it.',
} as const;

interface Entitlement {
  source: string;
  paidBy?: { name: string } | null;
  billingHold?: boolean;
  billingHoldReason?: keyof typeof HOLD_MESSAGE | null;
  autoTopUpPackId?: string | null;
  hasBillingCustomer?: boolean;
  autoTopUpAvailable?: boolean;
}

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export function BillingSettings({ checkout }: { checkout?: string }) {
  const [ent, setEnt] = useState<Entitlement | null>(null);
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [welcomeCredits, setWelcomeCredits] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [packs, setPacks] = useState<CreditPacksResponse | null>(null);
  const [cancelledShown, setCancelledShown] = useState(checkout === 'cancelled');

  useEffect(() => {
    Promise.all([api.get('/billing/entitlement'), billingAPI.getBalance()])
      .then(([e, b]) => {
        setEnt(e.data);
        setBalance(b);
      })
      .catch((err) => {
        const raw = err?.response?.data?.error;
        setError(typeof raw === 'string' ? raw : raw?.message || 'Could not load billing info');
      });
    billingAPI.getActivity({ kind: 'grant', limit: 100 })
      .then(({ entries }) => setWelcomeCredits(entries.find((e) => e.label === WELCOME_CREDITS_LABEL)?.credits ?? null))
      .catch(() => setWelcomeCredits(null));
    billingAPI.getPacks().then(setPacks).catch(() => setPacks(null));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!ent || !balance) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;

  const available = balance.availableMilli / 1000;
  const orgPays = !!ent.paidBy;
  const purchased = ent.source === 'STRIPE' || !!ent.hasBillingCustomer;
  const videos = videosFor(balance.availableMilli / 1000, packs?.packs) >= 1 ? aboutVideos(balance.availableMilli / 1000, packs?.packs).toLowerCase() : null;
  const note = orgPays
    ? `Paid from ${ent.paidBy!.name}'s wallet`
    : [!purchased && welcomeCredits !== null && `Includes your ${formatCreditAmount(welcomeCredits)} free credits`, videos && `enough for ${videos}`].filter(Boolean).join(' · ');

  return (
    <div className="grid gap-4">
      {cancelledShown && (
        <div role="status" className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span>Checkout cancelled. No charge was made.</span>
          <button type="button" aria-label="Dismiss" onClick={() => setCancelledShown(false)} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <SettingsPanel>
        <div data-testid="balance-hero" className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Available</div>
            <div className="mt-1 text-4xl font-extrabold tabular-nums tracking-tight">
              {formatCreditAmount(available)} <span className="text-lg font-semibold text-muted-foreground">credits</span>
            </div>
            {note && <p className="mt-2 text-sm text-muted-foreground">{note}</p>}
            {ent.billingHold && <p className="mt-2 text-sm text-destructive">{HOLD_MESSAGE[ent.billingHoldReason ?? 'NEGATIVE_BALANCE']}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => scrollTo(ACTIVITY_ANCHOR)}>View activity</Button>
            {!orgPays && <Button size="sm" onClick={() => scrollTo(BUY_ANCHOR)}>Buy credits</Button>}
          </div>
        </div>
      </SettingsPanel>

      {!orgPays && (
        <SettingsPanel id={BUY_ANCHOR}>
          <div>
            <h2 className="font-semibold">Buy credits</h2>
            <p className="text-sm text-muted-foreground">Credits never expire · every export is watermark-free, up to 4K</p>
          </div>
          <div id="buy-packs">
            <CreditPackPicker />
          </div>
          {ent.autoTopUpAvailable && (
            <AutoTopUp ent={ent} packs={packs?.packs ?? []} lowWater={balance.lowWaterMilli / 1000} />
          )}
        </SettingsPanel>
      )}

      <CreditActivity />

      {!orgPays && <PaymentsPanel hasCustomer={!!ent.hasBillingCustomer} />}

      <SettingsPanel>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <KeyRound className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Optional: use your own model key. Credits still apply.</p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/advanced">Manage key</Link>
          </Button>
        </div>
      </SettingsPanel>
    </div>
  );
}

function AutoTopUp({ ent, packs, lowWater }: { ent: Entitlement; packs: CreditPacksResponse['packs']; lowWater: number }) {
  const [packId, setPackId] = useState<string | null>(ent.autoTopUpPackId ?? null);
  const [choice, setChoice] = useState<string>(ent.autoTopUpPackId ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setChoice((c) => c || packs.find((p) => p.popular)?.id || packs[0]?.id || '');
  }, [packs]);

  const save = async (next: string | null) => {
    setSaving(true);
    setMessage(null);
    try {
      setPackId((await billingAPI.setAutoTopUp(next)).autoTopUpPackId);
    } catch (err) {
      const raw = (err as { response?: { data?: { error?: string | { message?: string } } } })?.response?.data?.error;
      setMessage((typeof raw === 'string' ? raw : raw?.message) || 'Could not save auto top-up');
    } finally {
      setSaving(false);
    }
  };

  const pack = packs.find((p) => p.id === choice);
  const canSave = !!ent.hasBillingCustomer;

  return (
    <div className="grid gap-2 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Auto top-up</h3>
          <p className="text-sm text-muted-foreground">
            When you drop below {formatCreditAmount(lowWater)} credits, buy {pack?.label ?? 'a pack'}. At most once a day.
          </p>
          {!canSave && <p className="text-xs text-muted-foreground">Available after your first purchase, once a card is saved.</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={choice} onValueChange={(v) => { setChoice(v); if (packId) void save(v); }} disabled={saving || packs.length === 0}>
            <SelectTrigger className="h-8 w-40" aria-label="Auto top-up pack"><SelectValue placeholder="Pack" /></SelectTrigger>
            <SelectContent>
              {packs.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Switch checked={!!packId} disabled={saving || !choice || !canSave} onCheckedChange={(on) => save(on ? choice : null)} aria-label="Auto top-up" />
        </div>
      </div>
      {message && <p className="text-sm text-destructive">{message}</p>}
    </div>
  );
}

function PaymentsPanel({ hasCustomer }: { hasCustomer: boolean }) {
  const [opening, setOpening] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const openPortal = async () => {
    setOpening(true);
    setMessage(null);
    try {
      window.location.href = (await billingAPI.openPortal()).url;
    } catch {
      setMessage('Could not open the billing portal');
      setOpening(false);
    }
  };

  return (
    <SettingsPanel>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold"><CreditCard className="h-4 w-4" /> Payments &amp; receipts</h2>
          <p className="text-sm text-muted-foreground">
            {hasCustomer ? 'Invoices, receipts and saved cards.' : 'Receipts and invoices appear here after your first purchase.'}
          </p>
        </div>
        {hasCustomer && (
          <Button variant="outline" size="sm" onClick={openPortal} disabled={opening}>
            {opening && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Manage in Stripe
          </Button>
        )}
      </div>
      {message && <p className="text-sm text-destructive">{message}</p>}
    </SettingsPanel>
  );
}
